import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { imageSizeExceededMessage, imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE } from '../../components/imageType';
import generateCaptcha from '../../components/captcha';
import Loader from '../../components/Loader';

const WithdrawCoupon = () => {
    // State for loader and data
    const [loader, setLoader] = useState(false);
    const [recordData, setRecordData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [couponTypes, setCouponTypes] = useState([]);

    // Modal states
    const [showFormModal, setShowFormModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'

    // Form data state
    const [formData, setFormData] = useState({
        title: '',
        details: '',
        amount: '',
        coin: '',
        couponType: '',
        status: true,
        oneTime: false // New field for one-time purchase
    });

    // Coupon type form data
    const [typeFormData, setTypeFormData] = useState({
        name: ''
    });

    // Form validation state
    const [formErrors, setFormErrors] = useState({});
    const [typeFormErrors, setTypeFormErrors] = useState({});

    // Image file state
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    // Fetch data functions
    const fetchData = async () => {
        try {
            setLoader(true);
            const resp = await Calling.getWithdrawCoupon();
            if (resp?.status) {
                setRecordData(resp?.data);
            } else {
                setRecordData([]);
                toast.error(resp?.message || 'Failed to fetch data');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong while fetching data');
        } finally {
            setLoader(false);
        }
    };

    const fetchCouponTypes = async () => {
        try {
            const resp = await Calling.getWithdrawCouponType();
            if (resp?.status) {
                setCouponTypes(resp?.data || []);
            } else {
                setCouponTypes([]);
                toast.error(resp?.message || 'Failed to fetch coupon types');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong while fetching coupon types');
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchData();
        fetchCouponTypes();
    }, []);

    // Toggle modal functions
    const toggleFormModal = () => {
        setShowFormModal(!showFormModal);
        if (!showFormModal) {
            // Reset form when opening
            resetForm();
        }
    };

    const toggleViewModal = () => {
        setShowViewModal(!showViewModal);
    };

    const toggleTypeModal = () => {
        setShowTypeModal(!showTypeModal);
        if (!showTypeModal) {
            setTypeFormData({ name: '' });
            setTypeFormErrors({});
        }
    };

    // Reset form function
    const resetForm = () => {
        setFormData({
            title: '',
            details: '',
            amount: '',
            coin: '',
            couponType: '',
            status: true,
            oneTime: false // Reset oneTime field
        });
        setImageFile(null);
        setImagePreview('');
        setCurrentItem(null);
        setFormErrors({});
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: null
            });
        }
    };

    const handleTypeInputChange = (e) => {
        const { name, value } = e.target;
        setTypeFormData({
            ...typeFormData,
            [name]: value
        });

        if (typeFormErrors[name]) {
            setTypeFormErrors({
                ...typeFormErrors,
                [name]: null
            });
        }
    };

    // Handle image file selection with validation
    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate file type
        if (!imageType.includes(file.type)) {
            setFormErrors({
                ...formErrors,
                image: invalidImageMessage
            });
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setFormErrors({
                ...formErrors,
                image: imageSizeExceededMessage
            });
            return;
        }

        // Clear error if valid
        setFormErrors({
            ...formErrors,
            image: null
        });

        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        }

        if (!formData.details.trim()) {
            errors.details = 'Details are required';
        }

        if (!formData.amount) {
            errors.amount = 'Amount is required';
        } else if (isNaN(formData.amount) || Number(formData.amount) <= 0) {
            errors.amount = 'Amount must be a positive number';
        }

        if (!formData.coin) {
            errors.coin = 'Coin is required';
        } else if (isNaN(formData.coin) || Number(formData.coin) <= 0) {
            errors.coin = 'Coin must be a positive number';
        }

        if (!formData.couponType) {
            errors.couponType = 'Coupon type is required';
        }

        if (formMode === 'add' && !imageFile) {
            errors.image = 'Image is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateTypeForm = () => {
        const errors = {};

        if (!typeFormData.name.trim()) {
            errors.name = 'Type name is required';
        }

        setTypeFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        // Validate form
        if (!validateForm()) {
            return;
        }

        try {
            setLoader(true);

            // Create FormData
            const formDataObj = new FormData();
            formDataObj.append('title', formData.title);
            formDataObj.append('details', formData.details);
            formDataObj.append('amount', formData.amount);
            formDataObj.append('coin', formData.coin);
            formDataObj.append('couponType', formData.couponType);
            formDataObj.append('oneTime', formData.oneTime); // Add oneTime field

            if (formMode === 'edit') {
                formDataObj.append('status', formData.status);
            }

            if (imageFile) {
                formDataObj.append('image', imageFile);
            }

            let resp;

            if (formMode === 'add') {
                resp = await Calling.insertWithdrawCoupon(formDataObj);
            } else {
                resp = await Calling.updateWithdrawCoupon(currentItem._id, formDataObj);
            }

            if (resp?.status) {
                toast.success(resp.message || 'Operation successful');
                toggleFormModal();
                fetchData();
            } else {
                toast.error(resp?.message || 'Operation failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle coupon type form submission
    const handleTypeSubmit = async (e) => {
        if (e) e.preventDefault();

        // Validate form
        if (!validateTypeForm()) {
            return;
        }

        try {
            setLoader(true);
            const resp = await Calling.addWithdrawCouponType(typeFormData);

            if (resp?.status) {
                toast.success(resp.message || 'Coupon type added successfully');
                toggleTypeModal();
                fetchCouponTypes();
                setTypeFormData({ name: '' });
            } else {
                toast.error(resp?.message || 'Failed to add coupon type');
            }
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle edit action
    const handleEdit = (item) => {
        setCurrentItem(item);
        setFormData({
            title: item.title,
            details: item.details,
            amount: item.amount,
            coin: item.coin,
            couponType: item.couponType,
            status: item.status,
            oneTime: item.oneTime || false // Set oneTime from item or default to false
        });
        setImagePreview(item.image);
        setFormMode('edit');
        setShowFormModal(true);
    };

    // Handle view action
    const handleView = (item) => {
        setCurrentItem(item);
        setShowViewModal(true);
    };

    // Handle select item for bulk actions
    const handleSelectItem = (id) => {
        setSelectedItems(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedItems.length === recordData.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(recordData.map(item => item._id));
        }
    };

    // Handle bulk delete
    const handleDelete = async () => {
        // Check if there are any items selected
        if (selectedItems.length === 0) {
            toast.error('No items selected');
            return;
        }

        try {
            // Show captcha verification
            const content = `You are about to delete ${selectedItems.length === 1 ? 'a coupon' : selectedItems.length + ' coupons'}. To confirm, please solve this math problem.`;
            const isCaptchaValid = generateCaptcha(content);

            // If captcha fails, stop the deletion process
            if (!isCaptchaValid) {
                toast.error('Captcha validation failed');
                return;
            }

            // Proceed with deletion if captcha is valid
            setLoader(true);
            const resp = await Calling.deleteWithdrawCoupon({ id: selectedItems });

            if (resp?.status) {
                toast.success(resp.message || 'Items deleted successfully');
                setSelectedItems([]);
                // Modal will be closed by the parent component if needed
                fetchData();
            } else {
                toast.error(resp?.message || 'Failed to delete items');
            }
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle delete coupon type
    const handleDeleteCouponType = async (typeId) => {
        try {
            // Show captcha verification
            const content = "You are about to delete a coupon type. To confirm, please solve this math problem.";
            const isCaptchaValid = generateCaptcha(content);

            // If captcha fails, stop the deletion process
            if (!isCaptchaValid) {
                toast.error('Captcha validation failed');
                return;
            }

            setLoader(true);
            const resp = await Calling.deleteWithdrawCouponType({ ids: [typeId] });

            if (resp?.status) {
                toast.success(resp.message || 'Coupon type deleted successfully');
                fetchCouponTypes();
            } else {
                toast.error(resp?.message || 'Failed to delete coupon type');
            }
        } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 mb-2 text-main-font">Redeem Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg overflow-hidden'>
                <Navbar selectState={'coupon'} />
                <hr className='border-main-border' />

                {/* Header Section */}
                <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h4 className="text-xl font-semibold text-main-font">Redeem Coupons</h4>
                    <div className="flex flex-wrap gap-2">
                        {selectedItems.length > 0 && (
                            <button
                                className="flex items-center gap-1 px-4 py-2.5 text-white bg-main-delete rounded-md hover:bg-opacity-90 transition shadow-sm"
                                onClick={handleDelete}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Selected ({selectedItems.length})
                            </button>
                        )}
                        <button
                            className="flex items-center gap-1 px-4 py-2.5 text-white bg-main-blue rounded-md hover:bg-opacity-90 transition shadow-sm"
                            onClick={() => {
                                setFormMode('add');
                                toggleFormModal();
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add New Coupon
                        </button>
                        <button
                            className="flex items-center gap-1 px-4 py-2.5 text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-md hover:opacity-90 transition shadow-sm"
                            onClick={toggleTypeModal}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            Manage Coupon Types
                        </button>
                    </div>
                </div>

                {/* Data Table */}
                <div className="px-4 pb-6">
                    {loader && recordData.length === 0 ? (
                        <Loader loader={loader} />
                    ) : recordData.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-main-subfont opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <p className="text-main-subfont mt-4 text-lg">No Redeem coupons found</p>
                            <button
                                className="mt-4 px-4 py-2 bg-main-blue text-white rounded-md hover:bg-opacity-90 transition"
                                onClick={() => {
                                    setFormMode('add');
                                    toggleFormModal();
                                }}
                            >
                                Add Your First Coupon
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg shadow">
                            <table className="min-w-full divide-y divide-main-border">
                                <thead className="bg-main-th">
                                    <tr>
                                        <th className="w-12 px-3 py-3.5 text-left">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-main-border focus:ring-main-blue"
                                                checked={selectedItems.length === recordData.length && recordData.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th className="w-16 px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Image
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Title
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Coin
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            One-time
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-3 py-3.5 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-main-border">
                                    {recordData.map((item) => (
                                        <tr key={item._id} className="hover:bg-main-thHover transition-colors">
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 rounded border-main-border focus:ring-main-blue"
                                                    checked={selectedItems.includes(item._id)}
                                                    onChange={() => handleSelectItem(item._id)}
                                                />
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <div className="h-12 w-12 rounded-md overflow-hidden bg-gray-100 border border-main-border">
                                                    <img
                                                        src={item.image}
                                                        alt={item.title}
                                                        className="h-full w-full object-cover"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-main-font">{item.title}</div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-main-font">₹{item.amount}</div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <div className="text-sm text-main-font">{item.coin}</div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <div className="text-sm text-main-font">
                                                    <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                                        {item.couponType}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${item.oneTime
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {item.oneTime ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${item.status
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {item.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-2">
                                                    <button
                                                        className="text-main-blue hover:text-opacity-80 bg-blue-50 p-2 rounded-full transition-colors"
                                                        onClick={() => handleView(item)}
                                                        title="View details"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className="text-main-yellow hover:text-opacity-80 bg-yellow-50 p-2 rounded-full transition-colors"
                                                        onClick={() => handleEdit(item)}
                                                        title="Edit coupon"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Loading indicator when fetching with existing data */}
                            {loader && recordData.length > 0 && (
                                <div className="absolute inset-0 bg-white bg-opacity-50 flex justify-center items-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-main-blue"></div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showFormModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={toggleFormModal}></div>

                        {/* Modal positioning */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        {/* Modal content */}
                        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                            <div>
                                {/* Modal header */}
                                <div className="bg-white px-6 py-4 border-b border-main-border">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-medium text-main-font">
                                            {formMode === 'add' ? 'Add New Redeem Coupon' : 'Edit Redeem Coupon'}
                                        </h3>
                                        <button
                                            type="button"
                                            className="text-main-subfont hover:text-main-font transition-colors"
                                            onClick={toggleFormModal}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Modal body */}
                                <div className="bg-white px-6 py-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Left column */}
                                        <div className="space-y-5">
                                            {/* Title field */}
                                            <div>
                                                <label htmlFor="title" className="block text-sm font-medium text-main-font mb-1">
                                                    Title <span className="text-main-delete">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    id="title"
                                                    className={`w-full rounded-md border ${formErrors.title ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 px-3 py-2.5`}
                                                    placeholder="Enter coupon title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    maxLength={50}
                                                />
                                                {formErrors.title && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.title}</p>
                                                )}
                                                <p className="mt-1 text-xs text-main-subfont">Max 50 characters</p>
                                            </div>

                                            {/* Amount field */}
                                            <div>
                                                <label htmlFor="amount" className="block text-sm font-medium text-main-font mb-1">
                                                    Amount <span className="text-main-delete">*</span>
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <span className="text-main-subfont">₹</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        name="amount"
                                                        id="amount"
                                                        className={`w-full rounded-md border ${formErrors.amount ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 pl-8 pr-3 py-2.5`}
                                                        placeholder="Enter amount"
                                                        value={formData.amount}
                                                        onChange={handleInputChange}
                                                        min="1"
                                                    />
                                                </div>
                                                {formErrors.amount && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.amount}</p>
                                                )}
                                            </div>

                                            {/* Coin field */}
                                            <div>
                                                <label htmlFor="coin" className="block text-sm font-medium text-main-font mb-1">
                                                    Coin <span className="text-main-delete">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    name="coin"
                                                    id="coin"
                                                    className={`w-full rounded-md border ${formErrors.coin ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 px-3 py-2.5`}
                                                    placeholder="Enter coin value"
                                                    value={formData.coin}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                />
                                                {formErrors.coin && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.coin}</p>
                                                )}
                                            </div>

                                            {/* Coupon Type field */}
                                            <div>
                                                <label htmlFor="couponType" className="block text-sm font-medium text-main-font mb-1">
                                                    Coupon Type <span className="text-main-delete">*</span>
                                                </label>
                                                <select
                                                    name="couponType"
                                                    id="couponType"
                                                    className={`w-full rounded-md border ${formErrors.couponType ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 px-3 py-2.5`}
                                                    value={formData.couponType}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Coupon Type</option>
                                                    {couponTypes.map(type => (
                                                        <option key={type._id} value={type.name}>
                                                            {type.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {formErrors.couponType && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.couponType}</p>
                                                )}
                                                {couponTypes.length === 0 && (
                                                    <p className="mt-1 text-xs text-main-subfont">
                                                        No coupon types available. <button type="button" className="text-main-blue" onClick={(e) => { e.preventDefault(); toggleTypeModal(); }}>Add a coupon type first</button>
                                                    </p>
                                                )}
                                            </div>

                                            {/* Status and One-time checkboxes */}
                                            <div className="mt-4 space-y-4">
                                                {/* One-time purchase checkbox */}
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        name="oneTime"
                                                        id="oneTime"
                                                        className="h-4 w-4 rounded border-main-border focus:ring-main-blue text-main-blue"
                                                        checked={formData.oneTime}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label htmlFor="oneTime" className="ml-2 block text-sm font-medium text-main-font">
                                                        One-time purchase only
                                                    </label>
                                                    <div className="ml-2 group relative">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-main-subfont cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <div className="hidden group-hover:block absolute left-0 bottom-6 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                                                            When enabled, users can redeem this coupon only once.
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Status toggle (only for edit mode) */}
                                                {formMode === 'edit' && (
                                                    <div className="flex items-center">
                                                        <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                                            <input
                                                                type="checkbox"
                                                                name="status"
                                                                id="status"
                                                                className="opacity-0 absolute h-0 w-0"
                                                                checked={formData.status}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label
                                                                htmlFor="status"
                                                                className={`${formData.status ? 'bg-main-blue' : 'bg-gray-300'} block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in`}
                                                            >
                                                                <span
                                                                    className={`${formData.status ? 'translate-x-4' : 'translate-x-0'} block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in`}
                                                                ></span>
                                                            </label>
                                                        </div>
                                                        <span className="text-sm text-main-font ml-2">
                                                            {formData.status ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right column */}
                                        <div className="space-y-5">
                                            {/* Details field */}
                                            <div>
                                                <label htmlFor="details" className="block text-sm font-medium text-main-font mb-1">
                                                    Details <span className="text-main-delete">*</span>
                                                </label>
                                                <textarea
                                                    name="details"
                                                    id="details"
                                                    rows="4"
                                                    className={`w-full rounded-md border ${formErrors.details ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 px-3 py-2.5`}
                                                    placeholder="Enter coupon details"
                                                    value={formData.details}
                                                    onChange={handleInputChange}
                                                    maxLength={400}
                                                ></textarea>
                                                {formErrors.details && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.details}</p>
                                                )}
                                                <div className="mt-1 flex justify-between">
                                                    <p className="text-xs text-main-subfont">Max 400 characters</p>
                                                    <p className="text-xs text-main-subfont">{formData.details.length}/400</p>
                                                </div>
                                            </div>

                                            {/* Image upload field */}
                                            <div>
                                                <label htmlFor="image" className="block text-sm font-medium text-main-font mb-1">
                                                    Image {formMode === 'add' && <span className="text-main-delete">*</span>}
                                                </label>
                                                <div className={`border-2 border-dashed rounded-md px-6 py-8 ${formErrors.image ? 'border-main-delete bg-red-50' : 'border-main-border bg-main-bg'}`}>
                                                    <div className="text-center">
                                                        {!imagePreview ? (
                                                            <>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-main-subfont" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                <p className="mt-1 text-sm text-main-subfont">
                                                                    Drag and drop or click to select
                                                                </p>
                                                                <p className="text-xs text-main-subfont mt-1">
                                                                    PNG, JPG, JPEG (Max: 1MB)
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <div className="relative">
                                                                <img
                                                                    src={imagePreview}
                                                                    alt="Preview"
                                                                    className="max-h-32 mx-auto rounded-md"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-100 text-main-delete p-1 rounded-full"
                                                                    onClick={() => {
                                                                        setImageFile(null);
                                                                        setImagePreview('');
                                                                    }}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        )}
                                                        <input
                                                            type="file"
                                                            name="image"
                                                            id="image"
                                                            className="hidden"
                                                            onChange={handleImageChange}
                                                            accept={imageType.join(',')}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="mt-4 inline-flex items-center px-3 py-2 border border-main-border rounded-md shadow-sm text-sm font-medium text-main-font bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-blue"
                                                            onClick={() => document.getElementById('image').click()}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5 text-main-subfont" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                            </svg>
                                                            {imagePreview ? 'Change Image' : 'Browse Image'}
                                                        </button>
                                                    </div>
                                                </div>
                                                {formErrors.image && (
                                                    <p className="mt-1 text-sm text-main-delete">{formErrors.image}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal footer */}
                                <div className="bg-main-bg px-6 py-4 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-main-font bg-white border border-main-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-blue transition-colors"
                                        onClick={toggleFormModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="inline-flex justify-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-white bg-main-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-blue transition-colors"
                                        disabled={loader}
                                    >
                                        {loader ? (
                                            <div className="flex items-center">
                                                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></div>
                                                {formMode === 'add' ? 'Creating...' : 'Updating...'}
                                            </div>
                                        ) : formMode === 'add' ? 'Create Coupon' : 'Update Coupon'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && currentItem && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={toggleViewModal}></div>

                        {/* Modal positioning */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        {/* Modal content */}
                        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                            {/* Modal header */}
                            <div className="bg-white px-6 py-4 border-b border-main-border">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-main-font">
                                        Redeem Coupon Details
                                    </h3>
                                    <button
                                        type="button"
                                        className="text-main-subfont hover:text-main-font transition-colors"
                                        onClick={toggleViewModal}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Modal body */}
                            <div className="bg-white px-6 py-5">
                                <div className="bg-main-th p-5 rounded-lg mb-6">
                                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                                        <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-white shadow-md mx-auto md:mx-0">
                                            <img
                                                src={currentItem.image}
                                                alt={currentItem.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 text-center md:text-left">
                                            <h2 className="text-xl font-semibold text-main-font">{currentItem.title}</h2>
                                            <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-3">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-main-blue bg-opacity-10 text-main-blue text-sm">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    ₹{currentItem.amount}
                                                </span>
                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-main-yellow bg-opacity-10 text-main-yellow text-sm">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {currentItem.coin} Coins
                                                </span>
                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                    </svg>
                                                    {currentItem.couponType}
                                                </span>
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${currentItem.oneTime
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {currentItem.oneTime ? 'One-time Only' : 'Multiple Purchases'}
                                                </span>
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${currentItem.status
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {currentItem.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-md font-semibold text-main-font mb-2">Details</h4>
                                    <div className="bg-main-bg p-4 rounded-md">
                                        <p className="text-main-font whitespace-pre-line">{currentItem.details}</p>
                                    </div>
                                </div>

                                <div className="mt-6 flex gap-2">
                                    <button
                                        type="button"
                                        className="flex-1 inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-main-yellow bg-main-yellow bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-yellow transition-colors"
                                        onClick={() => {
                                            toggleViewModal();
                                            handleEdit(currentItem);
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit Coupon
                                    </button>
                                </div>
                            </div>

                            {/* Modal footer */}
                            <div className="bg-main-bg px-6 py-4 flex justify-end">
                                <button
                                    type="button"
                                    className="inline-flex justify-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-main-font bg-white border border-main-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-blue transition-colors"
                                    onClick={toggleViewModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Coupon Type Modal */}
            {showTypeModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={toggleTypeModal}></div>

                        {/* Modal positioning */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        {/* Modal content */}
                        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            {/* Modal header */}
                            <div className="bg-white px-6 py-4 border-b border-main-border">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-main-font">
                                        Manage Coupon Types
                                    </h3>
                                    <button
                                        type="button"
                                        className="text-main-subfont hover:text-main-font transition-colors"
                                        onClick={toggleTypeModal}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Modal body */}
                            <div className="bg-white px-6 py-5">
                                {/* Add coupon type form */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                name="name"
                                                id="typeName"
                                                className={`w-full rounded-md border ${typeFormErrors.name ? 'border-main-delete' : 'border-main-border'} focus:border-main-blue focus:ring focus:ring-main-blue focus:ring-opacity-50 px-3 py-2.5`}
                                                placeholder="Enter new coupon type"
                                                value={typeFormData.name}
                                                onChange={handleTypeInputChange}
                                                maxLength={50}
                                            />
                                            {typeFormErrors.name && (
                                                <p className="mt-1 text-sm text-main-delete">{typeFormErrors.name}</p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleTypeSubmit}
                                            className="inline-flex justify-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                            disabled={loader}
                                        >
                                            {loader ? (
                                                <div className="flex items-center">
                                                    <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></div>
                                                    Adding...
                                                </div>
                                            ) : 'Add Type'}
                                        </button>
                                    </div>
                                </div>

                                {/* List of existing coupon types */}
                                <div className="border border-main-border rounded-md overflow-hidden">
                                    <div className="bg-main-th px-4 py-3">
                                        <h4 className="text-sm font-medium text-main-font">Existing Coupon Types</h4>
                                    </div>
                                    {couponTypes.length === 0 ? (
                                        <div className="p-6 text-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-main-subfont opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                            </svg>
                                            <p className="mt-2 text-main-subfont">No coupon types added yet</p>
                                        </div>
                                    ) : (
                                        <ul className="divide-y divide-main-border">
                                            {couponTypes.map(type => (
                                                <li key={type._id} className="px-4 py-3 flex items-center justify-between">
                                                    <span className="text-main-font">{type.name}</span>
                                                    <button
                                                        type="button"
                                                        className="text-main-delete hover:text-opacity-80 bg-red-50 p-1.5 rounded-full transition-colors"
                                                        onClick={() => handleDeleteCouponType(type._id)}
                                                        title="Delete type"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Modal footer */}
                            <div className="bg-main-bg px-6 py-4 flex justify-end">
                                <button
                                    type="button"
                                    className="inline-flex justify-center px-4 py-2.5 text-sm font-medium rounded-md shadow-sm text-main-font bg-white border border-main-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-blue transition-colors"
                                    onClick={toggleTypeModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawCoupon;